import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  apiClient,
  extractErrorMessage,
  saveTokens,
  type AuthTokens,
} from "@/lib/api-client";
import { useAuthStore, type Account } from "@/stores/auth-store";

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 초대 링크에서 온 경우 이메일 prefill.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefillEmail = params.get("email");
    if (prefillEmail) setEmail(prefillEmail);
  }, [location.search]);

  // idle timeout / 비번 재설정 직후 등 redirect 사유에 따른 안내.
  const reason = (location.state as { reason?: string } | null)?.reason;
  const reasonNotice =
    reason === "idle-timeout"
      ? "한동안 활동이 없어 보안을 위해 자동 로그아웃했어요."
      : reason === "password-reset"
        ? "비밀번호를 변경했어요. 새 비밀번호로 로그인해 주세요."
        : null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data: tokens } = await apiClient.post<AuthTokens>("/auth/login", {
        email,
        password,
      });
      // 토큰부터 저장 — 후속 /auth/me 가 Authorization 헤더 사용 가능하도록.
      saveTokens(tokens);
      const { data: account } = await apiClient.get<Account>("/auth/me");
      setSession(tokens, account);
      // 초대 토큰이 query 에 있으면 수락 페이지로 자동 redirect.
      const params = new URLSearchParams(location.search);
      const invitation = params.get("invitation");
      if (invitation) {
        navigate(
          `/invitations/accept?token=${encodeURIComponent(invitation)}`,
          { replace: true },
        );
      } else {
        navigate("/app");
      }
    } catch (err) {
      setError(extractErrorMessage(err, "로그인에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="container flex min-h-[calc(100vh-3.5rem)] items-center justify-center py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight">다시 만났네요</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            오늘도 한 PR 더 가까이 가 봅시다.
          </p>
        </div>
        {reasonNotice && (
          <p className="mb-4 rounded-md border border-orange-500/30 bg-orange-500/10 px-3 py-2 text-sm">
            {reasonNotice}
          </p>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="text-sm font-medium">
              이메일
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              className="mt-1.5"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className="text-sm font-medium">
              비밀번호
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              className="mt-1.5"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            로그인
          </Button>
        </form>
        <p className="mt-4 text-center text-sm">
          <Link
            to="/forgot-password"
            className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            비밀번호를 잊으셨나요?
          </Link>
        </p>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          계정이 없으신가요?{" "}
          <Link to="/signup" className="font-medium text-foreground underline-offset-4 hover:underline">
            회원가입
          </Link>
        </p>
      </div>
    </main>
  );
}
